/**
 * EdgFlow — the render-only React Flow EDG (phase 6b, step 1).
 *
 * Fully CONTROLLED: every node is DERIVED from the project's uimodel EDG store
 * (`model.uimodel.edg.nodes`) + the metamodel object types, inside a mobx
 * `observer` so the derivation re-runs when any observable it touches changes.
 * MST stays the single source of truth; this component performs ZERO mutations
 * (6b is render-only — no drag, no tools, no editing).
 *
 * Parity targets (ported from `components/EDG/index.js` + `lib/joint`):
 *  - Nodes come from the uimodel node positions/sizes; the object type supplies
 *    name + abstract. The level shown follows the EDG `show_bul` / `show_tdl`
 *    tool (the SAME observable the old EDG read: `ui.edg.activeTool` →
 *    `objectType.bul` / `objectType.tdl`).
 *  - Notation (MERODE/UML) comes from `project.model.notation`, threaded in as a
 *    prop exactly like the old `ProjectVersion → EDG` wiring.
 *  - Highlight classes (`joint-highlighted{,-path,-primary,-secondary}`) are
 *    derived from the four observable id-lists on the uimodel/EDG UI store
 *    (`highlighted`, `highlightedPath`, `primaryPath`, `secondaryPath`), matching
 *    the className mapping in the old paper.
 *
 * Per-node object identity is cached by node id + a cheap revision hash of the
 * derived data, so unchanged nodes keep the SAME object reference across renders
 * and React Flow does not re-render every node on every observable tick.
 *
 * `nodeTypes` is defined at MODULE level (React Flow requirement — an inline
 * object would reset the node renderers on every render).
 *
 * Interactions are OFF in 6b: nodesDraggable / nodesConnectable /
 * elementsSelectable are all false. Pan-on-blank-drag + wheel zoom stay on
 * (panOnDrag, zoomOnScroll) with the min/max zoom from the old paper.
 *
 * Classic JSX runtime (app-wide esbuild transform) needs React in scope.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import {
  ReactFlow,
  ReactFlowProvider,
  ViewportPortal,
  Panel,
  ConnectionMode,
  ConnectionLineType,
  useReactFlow,
  type Connection,
  type NodeChange,
  type NodeMouseHandler,
  type EdgeMouseHandler,
  type OnNodeDrag,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './edgFlow.css';

import ObjectTypeNode, {
  type Notation,
  type ObjectTypeFlowNode,
  type HighlightClass,
} from './ObjectTypeNode';
import DependencyEdge from './DependencyEdge';
import InheritanceEdge from './InheritanceEdge';
import { type EdgeFlowEdge, type EdgeLabel } from './EdgeBase';
import { type VertexDragLive } from './VertexHandles';
import { computeEdgePath, pointAtLength, type Rect, type Pt } from './edgeGeometry';
import { withEdgEdgeZIndex } from './edgeStacking';
import { applyControlledNodeChanges, flowNodeRects } from './flowNodeState';
import {
  commitVertexInsert,
  planVertexInsert,
  removeVertex,
  type VertexSelection,
  vertexHintText,
} from './vertexEditing';
import {
  ZOOM_ON_DOUBLE_CLICK,
  consumeDiagramDoubleClick,
  isDoubleClickFollowUp,
  recoverVertexInsertFromPaneClick,
  rememberEdgeClick,
  resolveDoubleClickInsert,
  type PendingEdgeClick,
} from './diagramDoubleClick';
import {
  dependencyMarkers,
  inheritanceMarker,
  dependencyLabels,
  peerLabelForDependency,
  type DependencyType,
  type EdgeHighlight,
} from './edgeMapping';
import { MerodeMarkerDefs } from './markers';
import EdgZoomToolbar, { MIN_ZOOM, MAX_ZOOM } from './EdgZoomToolbar';

// React Flow requires stable, module-level type maps.
const nodeTypes = { objectType: ObjectTypeNode } as const;
const edgeTypes = { dependency: DependencyEdge, inheritance: InheritanceEdge } as const;

// The object-type rect stroke-width (matches createEDGNode / ObjectTypeNode).
const OBJECT_TYPE_STROKE = 2;

/**
 * The uimodel/EDG UI store exposes four observable id-lists. We resolve a single
 * highlight class per object id with the same precedence the old paper used
 * (primary > secondary > path > default). The old EDG applied each class
 * independently via separate highlighters, but for a node only one visible style
 * wins; primary/secondary are MPC paths and take precedence over the plain
 * selection highlight.
 */
function highlightClassFor(
  id: string,
  highlighted: string[],
  highlightedPath: string[],
  primaryPath: string[],
  secondaryPath: string[]
): HighlightClass {
  if (primaryPath.includes(id)) return 'joint-highlighted-primary';
  if (secondaryPath.includes(id)) return 'joint-highlighted-secondary';
  if (highlightedPath.includes(id)) return 'joint-highlighted-path';
  if (highlighted.includes(id)) return 'joint-highlighted';
  return null;
}

/**
 * Resolve the level string to show for an object type, mirroring the old EDG's
 * `nodeLevel`: BUL when the `show_bul` tool is active, TDL when `show_tdl` is
 * active, otherwise no level.
 */
function resolveLevel(
  objectType: { bul: number; tdl: number },
  activeTool: string | null
): string | undefined {
  if (activeTool === 'show_bul') return `${objectType.bul}`;
  if (activeTool === 'show_tdl') return `${objectType.tdl}`;
  return undefined;
}

export type EdgFlowProps = {
  /** The MERODE model (a `version.content`). Read-only here. */
  // The legacy MST stores are untyped .js; `any` is the honest boundary type.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  model: any;
  /** Active notation (from `project.model.notation`). */
  notation: Notation;
  /** The uimodel/EDG UI store (`store.ui.edg`). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ui: any;

  // --- Interactions (6c). All OPTIONAL: when omitted the flow stays render-only
  // (the 6b behaviour). Cutover (step 3) wires these to ProjectVersion's
  // existing confirmable handlers; DevEdg wires the same store flow today. ---

  /**
   * Enable node selection / drag / resize and edge selection. When false (the
   * default) the flow is render-only — every interaction prop is ignored.
   */
  interactive?: boolean;
  /**
   * Currently selected element id (from `store.ui.selected`). Drives which node
   * shows the React Flow `selected` flag (and thus the FreeTransform resizer)
   * and which edge is marked selected. The orange highlight itself still comes
   * from the `highlighted` id-list, exactly like the old EDG.
   */
  selectedId?: string | null;
  /** Select an element by id. Routes through `ui.select` upstream. */
  onSelect?: (id: string) => void;
  /** Blank-canvas click. Deselects (and arms add-object upstream if active). */
  onPaneClick?: (x: number, y: number) => void;
  /**
   * Start the add-object flow at a canvas point (the existing
   * `confirmable('Add object')` → AddObjectController flow). Called by
   * `onPaneClick` only when the `add_object` tool is armed, exactly like the old
   * EDG. The tool STAYS armed afterwards (multi-add), matching the baseline.
   */
  onAddObject?: (position: { x: number; y: number }) => void;
  /**
   * Create a dependency from a connect-drag (the existing
   * `confirmable('Add dependency')` → AddDependencyController flow). Called when a
   * node-to-node connect lands while the `add_dependency` tool is armed; `type` is
   * the active dependency type. Validation (self-loop / duplicate / propagation)
   * lives in the command and surfaces through the existing report flow.
   */
  onAddDependency?: (sourceId: string, targetId: string, type: string) => void;
  /**
   * Create an inheritance from a connect-drag (the existing
   * `confirmable('Add inheritance relation')` flow). Called when a node-to-node
   * connect lands while the `add_inheritance` tool is armed. Self-loops are
   * rejected here (the old `onLinkConnected` guarded `sourceId !== targetId`).
   */
  onAddInheritance?: (sourceId: string, targetId: string) => void;
  /**
   * Run a dagre auto-layout in the given direction (the BUL/TDL layout buttons).
   * 'BT' = Auto-layout Bottom Up, 'TD' = Auto-layout Top Down. Clears vertices and
   * commits every node position through the existing commands, matching the OLD
   * EDG's per-node `updateEDGPosition` history semantics. Wired by the caller;
   * EdgToolbar renders the buttons that invoke it.
   */
  onLayout?: (direction: 'BT' | 'TD') => void;
  /** Commit a node's final position on drag end (existing `updateEDGPosition`). */
  onChangePosition?: (objectTypeId: string, x: number, y: number) => void;
  /** Commit a node's final size on resize end (existing `updateEDGSize`). */
  onChangeSize?: (objectTypeId: string, width: number, height: number) => void;
  /**
   * Replace an edge's vertices (existing `updateEDGVertices` /
   * `updateEDGInheritanceVertices`). `kind` distinguishes the two link stores.
   */
  onChangeVertices?: (
    edgeId: string,
    kind: 'dependency' | 'inheritance',
    vertices: Pt[]
  ) => void;
  /**
   * Class for the floating zoom toolbar, mirroring the old EDG's
   * `floatingToolbarClassName` (`''` when more than one panel is visible, else
   * `'shifted'`). Defaults to `'shifted'` so the dev surface and a single-panel
   * layout match the old standalone EDG.
   */
  floatingToolbarClassName?: string;
  /**
   * EXPORT mode (phase 6e). When true the flow renders 1:1 at zoom 1 with NO
   * fitView, NO interactions and NO floating UI (zoom toolbar / vertex hint), so
   * the off-screen PNG exporter can measure the true content bbox and crop to it.
   * The old Rappid `toPNG` rasterised at scale 1; this mode reproduces that.
   */
  exportMode?: boolean;
};

type NodeCache = Map<string, { rev: string; node: ObjectTypeFlowNode }>;

/**
 * Derive the React Flow nodes from the model + uimodel + EDG UI store.
 *
 * This is called DIRECTLY in the render body of the `observer` component (NOT
 * memoised): every observable it touches — object-type names/abstract, uimodel
 * node positions/sizes, `ui.activeTool`, and the four highlight id-lists — must
 * be read inside the reactive render so mobx re-renders the component (and
 * re-derives) when any of them change. A `useMemo` keyed on the stable `ui` /
 * `model` MST references would NOT recompute on a deep observable change such as
 * `activeTool` flipping to `show_bul`, so per-node identity stability is handled
 * by the revision-hash cache instead, not by memo dep gating.
 */
type NodeInteraction = {
  interactive: boolean;
  selectedId: string | null;
  onResizeEnd?: (objectTypeId: string, width: number, height: number) => void;
};

function deriveNodes(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  model: any,
  notation: Notation,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ui: any,
  cache: NodeCache,
  interaction: NodeInteraction
): ObjectTypeFlowNode[] {
  if (!model) return [];

  const { interactive, selectedId, onResizeEnd } = interaction;
  const activeTool: string | null = ui ? ui.activeTool : null;
  // FreeTransform parity: the old EDG enabled resize only when NO tool was
  // active (`resizeable = activeTool === null`). Mirror that here.
  const resizeEnabled = interactive && activeTool === null;
  // Connect parity: the old EDG made a node connectable (a link magnet) only with
  // the add_dependency / add_inheritance tool armed (object_type.js:setConnectable
  // — `connectable = activeTool === 'add_dependency' || 'add_inheritance'`). With
  // a connect tool armed, the node exposes a full-node connect handle; otherwise
  // the handles stay inert (selection / drag mode).
  const connectEnabled =
    interactive && (activeTool === 'add_dependency' || activeTool === 'add_inheritance');
  // The four observable highlight id-lists (read so the observer tracks them).
  const highlighted: string[] = ui && ui.highlighted ? [...ui.highlighted] : [];
  const highlightedPath: string[] = ui && ui.highlightedPath ? [...ui.highlightedPath] : [];
  const primaryPath: string[] = ui && ui.primaryPath ? [...ui.primaryPath] : [];
  const secondaryPath: string[] = ui && ui.secondaryPath ? [...ui.secondaryPath] : [];

  const uiEdg = model.uimodel ? model.uimodel.edg : null;
  const nextCache: NodeCache = new Map();
  const result: ObjectTypeFlowNode[] = [];

  for (const ot of model.metamodel.objectTypes) {
    const id: string = ot.id;
    // Look up the persisted uimodel node WITHOUT creating one (render-only).
    const uiNode = uiEdg ? uiEdg.findNode(ot) : null;
    // Fall back to the Node model defaults (0,0 / 100x50) if not persisted.
    const x = uiNode ? uiNode.position.x : 0;
    const y = uiNode ? uiNode.position.y : 0;
    const width = uiNode ? uiNode.size.width : 100;
    const height = uiNode ? uiNode.size.height : 50;

    const level = resolveLevel(ot, activeTool);
    const highlight = highlightClassFor(
      id,
      highlighted,
      highlightedPath,
      primaryPath,
      secondaryPath
    );

    const isSelected = interactive && selectedId === id;

    // Revision hash: identity changes only when something visible changes.
    const rev = [
      ot.name,
      ot.abstract ? '1' : '0',
      notation,
      level ?? '',
      x,
      y,
      width,
      height,
      highlight ?? '',
      interactive ? '1' : '0',
      resizeEnabled ? '1' : '0',
      connectEnabled ? '1' : '0',
      isSelected ? '1' : '0',
    ].join('|');

    const cached = cache.get(id);
    let node: ObjectTypeFlowNode;
    if (cached && cached.rev === rev) {
      node = cached.node;
    } else {
      node = {
        id,
        type: 'objectType',
        position: { x, y },
        width,
        height,
        // Drag is enabled only with no tool armed (the old EDG moved a node only
        // when `activeTool === null`; with a tool armed the same drag pans or
        // creates a link). Selection is on whenever interactive.
        draggable: interactive && activeTool === null,
        selectable: interactive,
        // Connectable only with a connect tool armed; the node's full-node connect
        // handle is enabled by the same flag (ObjectTypeNode reads data.connectable).
        connectable: connectEnabled,
        selected: isSelected,
        data: {
          name: ot.name,
          abstract: ot.abstract,
          notation,
          level,
          width,
          height,
          highlight,
          resizable: resizeEnabled,
          connectable: connectEnabled,
          onResizeEnd: onResizeEnd ? (w, h) => onResizeEnd(id, w, h) : undefined,
        },
      };
    }
    nextCache.set(id, { rev, node });
    result.push(node);
  }

  // Swap the cache contents in place so the stable ref keeps the new entries.
  cache.clear();
  for (const [k, v] of nextCache) cache.set(k, v);
  return result;
}

function edgFlowNodeSignature(nodes: ObjectTypeFlowNode[]): string {
  return nodes
    .map((node) =>
      JSON.stringify([
        node.id,
        node.position.x,
        node.position.y,
        node.width,
        node.height,
        node.draggable,
        node.selectable,
        node.connectable,
        node.selected,
        node.data.name,
        node.data.abstract,
        node.data.notation,
        node.data.level ?? '',
        node.data.highlight ?? '',
        node.data.resizable,
        node.data.connectable,
      ])
    )
    .join('|');
}

type EdgeCache = Map<string, { rev: string; edge: EdgeFlowEdge }>;
type EdgNodeView = {
  position: Pt;
  size: { width: number; height: number };
};
type EdgViewWithNodes = {
  findNode: (objectType: { id: string }) => EdgNodeView | null;
};

function isTextEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tagName = target.tagName.toLowerCase();
  return (
    tagName === 'input' ||
    tagName === 'textarea' ||
    tagName === 'select' ||
    target.isContentEditable
  );
}

/**
 * Read the four observable highlight id-lists into an EdgeHighlight for a given
 * edge id. The membership precedence is decided downstream in `edgeMapping`
 * (matching the old `dependency.js` / `inheritance.js`), so here we only report
 * the raw flags. All four arrays are read so the observer tracks them.
 */
function edgeHighlightFor(
  id: string,
  highlighted: string[],
  highlightedPath: string[],
  primaryPath: string[],
  secondaryPath: string[]
): EdgeHighlight {
  return {
    highlighted: highlighted.includes(id),
    highlightedPath: highlightedPath.includes(id),
    primaryPath: primaryPath.includes(id),
    secondaryPath: secondaryPath.includes(id),
  };
}

/**
 * Resolve a single highlight CLASS for the edge stroke + labels (CSS recolour in
 * edgFlow.css). The marker colour is handled separately via the chosen variant
 * marker id; this class only drives stroke/label colour. Precedence matches the
 * marker selection in `edgeMapping` (highlighted > path > primary > secondary).
 */
function edgeHighlightClass(h: EdgeHighlight): HighlightClass {
  if (h.highlighted) return 'joint-highlighted';
  if (h.highlightedPath) return 'joint-highlighted-path';
  if (h.primaryPath) return 'joint-highlighted-primary';
  if (h.secondaryPath) return 'joint-highlighted-secondary';
  return null;
}

/** Build the label list for a dependency at the persisted-path waypoints. */
function buildDependencyLabels(
  id: string,
  type: DependencyType,
  notation: Notation,
  peerLabel: string | undefined,
  points: Pt[]
): EdgeLabel[] {
  const spec = dependencyLabels(type, notation, peerLabel);
  const labels: EdgeLabel[] = [];
  // JointJS positions: source label at distance 20, centre at '50%', target at -20.
  if (spec.sourceLabel != null) {
    labels.push({ key: `${id}-s`, text: spec.sourceLabel, point: pointAtLength(points, 20) });
  }
  if (spec.centerLabel != null) {
    labels.push({ key: `${id}-c`, text: spec.centerLabel, point: pointAtLength(points, '50%') });
  }
  if (spec.targetLabel != null) {
    labels.push({ key: `${id}-t`, text: spec.targetLabel, point: pointAtLength(points, -20) });
  }
  return labels;
}

/** Resolve a node's rect from the uimodel (render-only — never creates a node). */
function nodeRect(
  uiEdg: EdgViewWithNodes | null,
  objectType: { id: string } | null,
  currentNodeRects: Map<string, Rect>
): Rect | null {
  if (!uiEdg || !objectType) return null;
  const currentRect = currentNodeRects.get(objectType.id);
  if (currentRect) return currentRect;

  const uiNode = uiEdg.findNode(objectType);
  if (uiNode) {
    return {
      x: uiNode.position.x,
      y: uiNode.position.y,
      width: uiNode.size.width,
      height: uiNode.size.height,
    };
  }
  // Fall back to the Node-model defaults (0,0 / 100x50) when not persisted.
  return { x: 0, y: 0, width: 100, height: 50 };
}

/**
 * Derive the React Flow edges from the model + uimodel + EDG UI store, mirroring
 * `EDG.renderDependencyEdges` + `renderInheritanceEdges` (components/EDG/index.js).
 *
 * Like deriveNodes, this is called in the observer's render body (NOT memoised)
 * so every observable read — vertices, node positions/sizes, notation,
 * abstract, the four highlight id-lists — re-runs the derivation when it changes.
 * Per-edge identity is kept stable through a revision-hash cache.
 */
type EdgeInteraction = {
  interactive: boolean;
  selectedId: string | null;
  selectedVertex: VertexSelection | null;
  /** Current React Flow node rects while dragging/resizing; MST rects are fallback. */
  currentNodeRects: Map<string, Rect>;
  /** Live vertex override per edge id during a handle drag (path follows). */
  liveVertices: Map<string, Pt[]>;
  onChangeVertices?: (
    edgeId: string,
    kind: 'dependency' | 'inheritance',
    vertices: Pt[]
  ) => void;
  onVertexDragLive: VertexDragLive;
  onSelectVertex: (
    edgeId: string,
    kind: 'dependency' | 'inheritance',
    index: number
  ) => void;
  onDeleteVertex: (
    edgeId: string,
    kind: 'dependency' | 'inheritance',
    index: number
  ) => void;
};

function deriveEdges(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  model: any,
  notation: Notation,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ui: any,
  cache: EdgeCache,
  interaction: EdgeInteraction
): EdgeFlowEdge[] {
  if (!model) return [];

  const {
    interactive,
    selectedId,
    selectedVertex,
    currentNodeRects,
    liveVertices,
    onChangeVertices,
    onVertexDragLive,
    onSelectVertex,
    onDeleteVertex,
  } = interaction;
  const highlighted: string[] = ui && ui.highlighted ? [...ui.highlighted] : [];
  const highlightedPath: string[] = ui && ui.highlightedPath ? [...ui.highlightedPath] : [];
  const primaryPath: string[] = ui && ui.primaryPath ? [...ui.primaryPath] : [];
  const secondaryPath: string[] = ui && ui.secondaryPath ? [...ui.secondaryPath] : [];

  const uiEdg = model.uimodel ? model.uimodel.edg : null;
  const nextCache: EdgeCache = new Map();
  const result: EdgeFlowEdge[] = [];

  const pushEdge = (id: string, rev: string, build: () => EdgeFlowEdge) => {
    const cached = cache.get(id);
    const edge = cached && cached.rev === rev ? cached.edge : build();
    nextCache.set(id, { rev, edge });
    result.push(edge);
  };

  // --- Dependencies (master -> dependent) -----------------------------------
  for (const dep of model.metamodel.dependencies) {
    const id: string = dep.id;
    const type = dep.type as DependencyType;
    const sourceRect = nodeRect(uiEdg, dep.master, currentNodeRects);
    const targetRect = nodeRect(uiEdg, dep.dependent, currentNodeRects);
    if (!sourceRect || !targetRect) continue;

    // Persisted vertices (incl. collision math) + peer label, exactly as
    // getDependencyProperties read them. During a live vertex drag the transient
    // override wins so the path follows the dragged handle (commit on release).
    const link = uiEdg ? uiEdg.findLinkById(id) : null;
    const persistedVertices: Pt[] = link
      ? link.jsVertices.map((v: Pt) => ({ x: v.x, y: v.y }))
      : [];
    const override = liveVertices.get(id);
    const vertices: Pt[] = override ?? persistedVertices;
    const peerLabel: string | undefined = peerLabelForDependency(dep);

    const h = edgeHighlightFor(id, highlighted, highlightedPath, primaryPath, secondaryPath);
    const highlight = edgeHighlightClass(h);
    const { markerStartId, markerEndId } = dependencyMarkers(type, notation, h);

    const path = computeEdgePath({
      sourceRect,
      targetRect,
      vertices,
      sourceStroke: OBJECT_TYPE_STROKE,
      targetStroke: OBJECT_TYPE_STROKE,
    });
    const labels = buildDependencyLabels(id, type, notation, peerLabel, path.points);

    const isSelected = interactive && selectedId === id;
    const selectedVertexIndex =
      isSelected && selectedVertex?.edgeId === id && selectedVertex.kind === 'dependency'
        ? selectedVertex.index
        : null;
    const rev = [
      'dep',
      type,
      notation,
      peerLabel ?? '',
      markerStartId ?? '',
      markerEndId ?? '',
      highlight ?? '',
      path.d,
      interactive ? '1' : '0',
      isSelected ? '1' : '0',
      selectedVertexIndex ?? '',
      // Vertices feed the handle positions; encode so the edge re-derives when the
      // persisted (or live-overridden) vertices change.
      isSelected ? JSON.stringify(vertices) : '',
    ].join('|');

    pushEdge(id, rev, () =>
      withEdgEdgeZIndex(
        {
          id,
          type: 'dependency',
          source: dep.master.id,
          target: dep.dependent.id,
          selectable: interactive,
          selected: isSelected,
          data: {
            d: path.d,
            markerStartId,
            markerEndId,
            highlight,
            labels,
            points: path.points,
            kind: 'dependency',
            vertices: interactive ? vertices : undefined,
            onChangeVertices: interactive ? onChangeVertices : undefined,
            onVertexDragLive: interactive ? onVertexDragLive : undefined,
            selectedVertexIndex,
            onSelectVertex: interactive ? onSelectVertex : undefined,
            onDeleteVertex: interactive ? onDeleteVertex : undefined,
          },
        },
        isSelected
      )
    );
  }

  // --- Inheritances (supertype -> subtype) ----------------------------------
  for (const inh of model.metamodel.inheritances) {
    const id: string = inh.id;
    const sourceRect = nodeRect(uiEdg, inh.supertype, currentNodeRects);
    const targetRect = nodeRect(uiEdg, inh.subtype, currentNodeRects);
    if (!sourceRect || !targetRect) continue;

    const link = uiEdg ? uiEdg.findInheritanceLinkById(id) : null;
    const persistedVertices: Pt[] = link
      ? link.jsVertices.map((v: Pt) => ({ x: v.x, y: v.y }))
      : [];
    const override = liveVertices.get(id);
    const vertices: Pt[] = override ?? persistedVertices;
    const abstract: boolean = inh.supertype.abstract;

    const h = edgeHighlightFor(id, highlighted, highlightedPath, primaryPath, secondaryPath);
    const highlight = edgeHighlightClass(h);
    const markerStartId = inheritanceMarker(abstract, notation, h);

    const path = computeEdgePath({
      sourceRect,
      targetRect,
      vertices,
      sourceStroke: OBJECT_TYPE_STROKE,
      targetStroke: OBJECT_TYPE_STROKE,
    });

    const isSelected = interactive && selectedId === id;
    const selectedVertexIndex =
      isSelected && selectedVertex?.edgeId === id && selectedVertex.kind === 'inheritance'
        ? selectedVertex.index
        : null;
    const rev = [
      'inh',
      notation,
      abstract ? '1' : '0',
      markerStartId ?? '',
      highlight ?? '',
      path.d,
      interactive ? '1' : '0',
      isSelected ? '1' : '0',
      selectedVertexIndex ?? '',
      isSelected ? JSON.stringify(vertices) : '',
    ].join('|');

    pushEdge(id, rev, () =>
      withEdgEdgeZIndex(
        {
          id,
          type: 'inheritance',
          source: inh.supertype.id,
          target: inh.subtype.id,
          selectable: interactive,
          selected: isSelected,
          data: {
            d: path.d,
            markerStartId,
            markerEndId: null,
            highlight,
            labels: [],
            points: path.points,
            kind: 'inheritance',
            vertices: interactive ? vertices : undefined,
            onChangeVertices: interactive ? onChangeVertices : undefined,
            onVertexDragLive: interactive ? onVertexDragLive : undefined,
            selectedVertexIndex,
            onSelectVertex: interactive ? onSelectVertex : undefined,
            onDeleteVertex: interactive ? onDeleteVertex : undefined,
          },
        },
        isSelected
      )
    );
  }

  cache.clear();
  for (const [k, v] of nextCache) cache.set(k, v);
  return result;
}

/**
 * Inner flow. Lives inside a <ReactFlowProvider> so the zoom toolbar's
 * `useReactFlow` / `useStore` hooks resolve against this flow's instance.
 */
const EdgFlowInner = observer(function EdgFlowInner({
  model,
  notation,
  ui,
  interactive = false,
  selectedId = null,
  onSelect,
  onPaneClick,
  onAddObject,
  onAddDependency,
  onAddInheritance,
  onChangePosition,
  onChangeSize,
  onChangeVertices,
  floatingToolbarClassName = 'shifted',
  exportMode = false,
}: EdgFlowProps) {
  // Caches keyed by element id, so unchanged nodes/edges keep their object
  // identity (revision-hash gate) across observable ticks.
  const nodeCacheRef = useRef<NodeCache>(new Map());
  const edgeCacheRef = useRef<EdgeCache>(new Map());

  const { screenToFlowPosition } = useReactFlow();

  // Transient live-vertex overrides during a handle drag, keyed by edge id. The
  // VertexHandles report the dragged point here so the edge path follows the
  // cursor; the MST commit lands on pointer-up (commit-on-drag-end). A ref drives
  // the derivation and a state counter forces the re-render (the derivation runs
  // in the observer body, which a plain ref mutation would not trigger).
  const liveVerticesRef = useRef<Map<string, Pt[]>>(new Map());
  const [, forceVertexRender] = useState(0);
  const onVertexDragLive = useCallback<VertexDragLive>((edgeId, _kind, vertices) => {
    if (vertices === null) liveVerticesRef.current.delete(edgeId);
    else liveVerticesRef.current.set(edgeId, vertices);
    forceVertexRender((n) => n + 1);
  }, []);
  const [selectedVertex, setSelectedVertex] = useState<VertexSelection | null>(null);

  const handleSelectVertex = useCallback(
    (edgeId: string, kind: 'dependency' | 'inheritance', index: number) => {
      setSelectedVertex({ edgeId, kind, index });
    },
    []
  );

  const handleDeleteVertex = useCallback(
    (edgeId: string, kind: 'dependency' | 'inheritance', index: number) => {
      if (!onChangeVertices) return;
      const link =
        kind === 'dependency'
          ? model?.uimodel?.edg?.findLinkById(edgeId)
          : model?.uimodel?.edg?.findInheritanceLinkById(edgeId);
      const existing: Pt[] = link ? link.jsVertices.map((v: Pt) => ({ x: v.x, y: v.y })) : [];
      if (index < 0 || index >= existing.length) return;
      liveVerticesRef.current.delete(edgeId);
      forceVertexRender((n) => n + 1);
      onChangeVertices(edgeId, kind, removeVertex(existing, index));
      setSelectedVertex(null);
    },
    [model, onChangeVertices]
  );

  // Resolve the resize-commit callback once (stable enough — closes over the
  // latest onChangeSize via the dep). Passed into each node's data.
  const onResizeEnd = useCallback(
    (objectTypeId: string, width: number, height: number) => {
      if (onChangeSize) onChangeSize(objectTypeId, width, height);
    },
    [onChangeSize]
  );

  // Bend-point hover hint — port of the old Paper
  // wrapper's `linkHover` state (ScrollablePaper.onLinkMouseOver/Out → the
  // `.link-help` div bottom-left). React Flow's edge enter/leave events fire
  // on the edge group, which now includes the wide invisible hit path.
  // Hovering a bend handle (part of the edge group) swaps in the remove hint.
  const [edgeHover, setEdgeHover] = useState(false);
  const [vertexHover, setVertexHover] = useState(false);
  const handleEdgeMouseEnter = useCallback(() => setEdgeHover(true), []);
  const handleEdgeMouseMove = useCallback((evt: React.MouseEvent) => {
    const target = evt.target instanceof Element ? evt.target : null;
    setVertexHover(target?.closest('.merode-vertex-handle') != null);
  }, []);
  const handleEdgeMouseLeave = useCallback(() => {
    setEdgeHover(false);
    setVertexHover(false);
  }, []);
  const vertexHint = vertexHintText({
    edgeHover,
    vertexHover,
    vertexSelected: selectedVertex != null,
  });

  const activeTool: string | null = ui ? ui.activeTool : null;
  // A connect tool is armed → node handles are live, drag-to-link creates a link
  // (and node drag / pane add are suppressed, matching the old tool gating).
  const connectArmed =
    interactive && (activeTool === 'add_dependency' || activeTool === 'add_inheritance');

  useEffect(() => {
    if (selectedVertex && selectedVertex.edgeId !== selectedId) setSelectedVertex(null);
  }, [selectedId, selectedVertex]);

  useEffect(() => {
    if (!interactive || !selectedVertex || !onChangeVertices) return;

    const handleKeyDown = (evt: KeyboardEvent) => {
      if (isTextEditingTarget(evt.target)) return;
      if (evt.key === 'Delete' || evt.key === 'Backspace') {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        handleDeleteVertex(selectedVertex.edgeId, selectedVertex.kind ?? 'dependency', selectedVertex.index);
      } else if (evt.key === 'Escape') {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        setSelectedVertex(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [handleDeleteVertex, interactive, onChangeVertices, selectedVertex]);

  // Derived on EVERY render (inside the observer) so mobx tracks all observable
  // reads — see deriveNodes for why this is intentionally not memoised.
  const baseNodes = deriveNodes(model, notation, ui, nodeCacheRef.current, {
    interactive,
    selectedId,
    onResizeEnd,
  });
  const baseNodesSignature = edgFlowNodeSignature(baseNodes);
  const latestBaseNodesRef = useRef<ObjectTypeFlowNode[]>(baseNodes);
  latestBaseNodesRef.current = baseNodes;
  const dragActiveRef = useRef(false);
  const [flowNodes, setFlowNodes] = useState<ObjectTypeFlowNode[]>(() => baseNodes);

  useEffect(() => {
    if (!dragActiveRef.current) setFlowNodes(latestBaseNodesRef.current);
  }, [baseNodesSignature]);

  const nodes = baseNodes.length === 0 ? baseNodes : flowNodes.length > 0 ? flowNodes : baseNodes;
  const currentNodeRects = flowNodeRects(nodes);
  const edges = deriveEdges(model, notation, ui, edgeCacheRef.current, {
    interactive,
    selectedId,
    selectedVertex,
    currentNodeRects,
    liveVertices: liveVerticesRef.current,
    onChangeVertices,
    onVertexDragLive,
    onSelectVertex: handleSelectVertex,
    onDeleteVertex: handleDeleteVertex,
  });
  const edgesRef = useRef(edges);
  edgesRef.current = edges;
  const pendingEdgeClickRef = useRef<PendingEdgeClick | null>(null);
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  const eventFlowPoint = useCallback(
    (evt: React.MouseEvent): Pt => {
      const flow = screenToFlowPosition({ x: evt.clientX, y: evt.clientY });
      return { x: Math.round(flow.x), y: Math.round(flow.y) };
    },
    [screenToFlowPosition]
  );

  // --- Vertex add: dblclick OR shift+click on an edge -----------------------
  // Ports `LinkView.pointerdblclick` / `pointerclick` (factories.js): insert a
  // vertex at the click point, in FLOW coordinates, then commit via the existing
  // vertex command. Insert next to the nearest rendered segment, matching the
  // bend order users expect when an edge already has multiple bends. Takes a
  // FLOW point, not the event: a double-click inserts at its first click,
  // captured before the inspector reflowed the panes.
  const insertVertex = useCallback(
    (edgeId: string, point: Pt) => {
      if (!onChangeVertices) return;
      const edge = edgesRef.current.find((item) => item.id === edgeId);
      if (!edge) return;
      const edgeData = edge.data as { kind?: string; points?: Pt[] } | undefined;
      const kind: 'dependency' | 'inheritance' =
        edgeData?.kind === 'inheritance' ? 'inheritance' : 'dependency';
      const link =
        kind === 'dependency'
          ? model?.uimodel?.edg?.findLinkById(edgeId)
          : model?.uimodel?.edg?.findInheritanceLinkById(edgeId);
      const existing: Pt[] = link ? link.jsVertices.map((v: Pt) => ({ x: v.x, y: v.y })) : [];
      const plan = planVertexInsert({
        edgeId,
        existing,
        points: edgeData?.points ?? [],
        point,
      });
      commitVertexInsert(plan, {
        persistVertices: (id, vertices) => onChangeVertices(id, kind, vertices),
        // ui.select toggles: re-selecting the selected edge would deselect it
        // and close the inspector.
        selectEdge: (id) => {
          if (onSelect && selectedIdRef.current !== id) onSelect(id);
        },
      });
      setSelectedVertex(null);
    },
    [onChangeVertices, onSelect, model]
  );

  // --- Selection ------------------------------------------------------------
  // Node/edge click routes through onSelect (which upstream calls ui.select +
  // the confirmable('Select') flow). Mirrors the old `onElementSelected`.
  const handleNodeClick: NodeMouseHandler = useCallback(
    (evt, node) => {
      if (!interactive) return;
      // The reflow slid a node under the second click of an edge double-click.
      const recovered = recoverVertexInsertFromPaneClick({
        pending: pendingEdgeClickRef.current,
        detail: evt.detail,
      });
      pendingEdgeClickRef.current = null;
      if (recovered && onChangeVertices && activeTool === null) {
        insertVertex(recovered.edgeId, recovered.point);
        return;
      }
      setSelectedVertex(null);
      // The old EDG selected only when NO tool was active.
      if (activeTool === null && onSelect) onSelect(node.id);
    },
    [interactive, activeTool, onSelect, onChangeVertices, insertVertex]
  );

  const handleEdgeClick: EdgeMouseHandler = useCallback(
    (evt, edge) => {
      if (!interactive) return;
      // Second click of a double-click: leave the selection alone (toggling it
      // off would close the inspector and reflow the panes again); the
      // dblclick handler inserts the knikpunt.
      if (isDoubleClickFollowUp({ pending: pendingEdgeClickRef.current, detail: evt.detail })) {
        return;
      }
      const point = eventFlowPoint(evt);
      pendingEdgeClickRef.current = rememberEdgeClick(edge.id, point);
      // Shift+click inserts a vertex (old `pointerclick`). The insert selects
      // the edge so the new knikpunt handle is visible even if it was not.
      if (evt.shiftKey) {
        insertVertex(edge.id, point);
        return;
      }
      setSelectedVertex(null);
      if (activeTool === null && onSelect) onSelect(edge.id);
    },
    [interactive, activeTool, onSelect, insertVertex, eventFlowPoint]
  );

  // Blank-canvas click. Ports `EDG.onPaperClick`: with the add_object tool armed
  // start the add-object flow at the click point (in FLOW coords), and ALWAYS
  // deselect (the old code called onSelect(null) on every blank click). The tool
  // stays armed afterwards for rapid multi-add, matching the baseline (§5).
  const handlePaneClick = useCallback(
    (evt: React.MouseEvent) => {
      if (!interactive) return;
      const recovered = recoverVertexInsertFromPaneClick({
        pending: pendingEdgeClickRef.current,
        detail: evt.detail,
      });
      pendingEdgeClickRef.current = null;
      if (recovered && onChangeVertices && activeTool === null) {
        consumeDiagramDoubleClick(evt);
        insertVertex(recovered.edgeId, recovered.point);
        return;
      }
      const flow = screenToFlowPosition({ x: evt.clientX, y: evt.clientY });
      if (activeTool === 'add_object' && onAddObject) {
        onAddObject({ x: Math.round(flow.x), y: Math.round(flow.y) });
      }
      setSelectedVertex(null);
      if (onPaneClick) onPaneClick(flow.x, flow.y);
    },
    [
      interactive,
      activeTool,
      onAddObject,
      onPaneClick,
      onChangeVertices,
      insertVertex,
      screenToFlowPosition,
    ]
  );

  // --- Connect mode (add_dependency / add_inheritance) ----------------------
  // Ports `ScrollablePaper.onLinkConnected` → `EDG.onLinkConnected`: when a
  // node-to-node connect lands, create a dependency (with the active type) or an
  // inheritance through the existing command. Inheritance rejects self-loops
  // client-side exactly like the old code (`sourceId !== targetId`); dependency
  // self-loops/duplicates are judged by the command + surfaced via its report.
  const handleConnect = useCallback(
    (connection: Connection) => {
      if (!interactive) return;
      const sourceId = connection.source;
      const targetId = connection.target;
      if (sourceId == null || targetId == null) return;
      if (activeTool === 'add_dependency' && onAddDependency) {
        const type: string = ui && ui.activeDependencyType ? ui.activeDependencyType : 'OPTIONAL_N';
        onAddDependency(sourceId, targetId, type);
      } else if (activeTool === 'add_inheritance' && sourceId !== targetId && onAddInheritance) {
        onAddInheritance(sourceId, targetId);
      }
    },
    [interactive, activeTool, ui, onAddDependency, onAddInheritance]
  );

  // --- Drag live via React Flow state → commit position on drag END ----------
  const handleNodeDragStart: OnNodeDrag = useCallback(() => {
    dragActiveRef.current = true;
  }, []);

  const handleNodeDragStop: OnNodeDrag = useCallback(
    (_evt, node, draggedNodes) => {
      if (!interactive) return;
      const movingNodes = draggedNodes.length > 0 ? draggedNodes : [node];
      if (onChangePosition) {
        for (const movingNode of movingNodes) {
          onChangePosition(
            movingNode.id,
            Math.round(movingNode.position.x),
            Math.round(movingNode.position.y)
          );
        }
      }
      dragActiveRef.current = false;
    },
    [interactive, onChangePosition]
  );

  const handleEdgeDoubleClick: EdgeMouseHandler = useCallback(
    (evt, edge) => {
      if (!interactive || !onChangeVertices) return;
      consumeDiagramDoubleClick(evt);
      // Shift+click already inserted on the first click of this double-click.
      if (evt.shiftKey) {
        pendingEdgeClickRef.current = null;
        return;
      }
      const target = resolveDoubleClickInsert({
        pending: pendingEdgeClickRef.current,
        edgeId: edge.id,
        point: eventFlowPoint(evt),
      });
      pendingEdgeClickRef.current = null;
      insertVertex(target.edgeId, target.point);
    },
    [interactive, onChangeVertices, insertVertex, eventFlowPoint]
  );

  // React Flow controlled-node contract: apply its transient position/dimension
  // changes locally, then persist model positions only on drag stop.
  const handleNodesChange = useCallback((changes: NodeChange<ObjectTypeFlowNode>[]) => {
    setFlowNodes((current) =>
      applyControlledNodeChanges(
        changes,
        current.length > 0 ? current : latestBaseNodesRef.current
      )
    );
  }, []);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      minZoom={MIN_ZOOM}
      maxZoom={MAX_ZOOM}
      // Interactions (6c) — gated by `interactive`; per-node/edge flags already
      // reflect the active tool (drag only with no tool armed).
      //
      // ConnectionMode.Loose lets a connect start/end on ANY handle regardless of
      // type, so the single source+target handle pair on each node accepts a
      // drag in both directions — matching the Rappid magnets-on-the-whole-node
      // behaviour. Connecting is enabled only while a connect tool is armed.
      connectionMode={ConnectionMode.Loose}
      connectionLineType={ConnectionLineType.Straight}
      nodesDraggable={interactive}
      nodesConnectable={connectArmed}
      elementsSelectable={interactive}
      selectNodesOnDrag={false}
      onNodesChange={interactive ? handleNodesChange : undefined}
      onNodeClick={interactive ? handleNodeClick : undefined}
      onNodeDragStart={interactive ? handleNodeDragStart : undefined}
      onNodeDragStop={interactive ? handleNodeDragStop : undefined}
      onConnect={interactive ? handleConnect : undefined}
      onEdgeClick={interactive ? handleEdgeClick : undefined}
      onEdgeDoubleClick={interactive ? handleEdgeDoubleClick : undefined}
      onEdgeMouseEnter={interactive ? handleEdgeMouseEnter : undefined}
      onEdgeMouseMove={interactive ? handleEdgeMouseMove : undefined}
      onEdgeMouseLeave={interactive ? handleEdgeMouseLeave : undefined}
      onPaneClick={interactive ? handlePaneClick : undefined}
      // Pan on blank-area drag + wheel zoom (the old paper behaviour), BUT not
      // while a connect tool is armed (a node-to-node drag must create a link, not
      // pan — the old paper set `pannable = activeTool !== 'add_object'` and the
      // connect drag started on a node magnet). Disabling pan while connecting also
      // stops a missed-handle drag from panning instead of cancelling the connect.
      panOnDrag={!connectArmed}
      zoomOnScroll={!exportMode}
      zoomOnPinch={!exportMode}
      zoomOnDoubleClick={ZOOM_ON_DOUBLE_CLICK}
      panOnScroll={false}
      preventScrolling={!exportMode}
      proOptions={{ hideAttribution: true }}
      // EXPORT mode: render 1:1 at zoom 1 (no fitView) so the off-screen PNG
      // exporter measures true content extents and crops; the old Rappid toPNG
      // rasterised at scale 1.
      fitView={!exportMode}
      fitViewOptions={{ padding: 0.2, minZoom: MIN_ZOOM, maxZoom: 1 }}
      defaultViewport={exportMode ? { x: 0, y: 0, zoom: 1 } : undefined}
    >
      {/*
        Markers MUST live inside the React Flow VIEWPORT subtree (the element the
        PNG export in 6e clones via html-to-image), not just anywhere in the flow.
        Plain children of <ReactFlow> mount in the renderer pane, OUTSIDE
        `.react-flow__viewport`, so we use <ViewportPortal> to inject the <defs>
        into `.react-flow__viewport-portal` — inside the captured subtree. The SVG
        is zero-size and absolutely positioned so it adds no layout.
      */}
      <ViewportPortal>
        <svg width={0} height={0} style={{ position: 'absolute' }} aria-hidden>
          <MerodeMarkerDefs />
        </svg>
      </ViewportPortal>
      {/*
        The floating zoom cluster MUST render through React Flow's <Panel>, which
        mounts it in `.react-flow__panel` (z-index:5) — ABOVE `.react-flow__pane`
        (z-index:1). A plain child of <ReactFlow> lands in the renderer pane at the
        pane's own z-index, so the pane (painted later) swallows real pointer
        clicks (elementFromPoint returns `react-flow__pane`); only synthetic
        `.click()` reached the handlers. The Panel fixes the stacking so the
        controls are clickable, while the toolbar keeps its own
        bottom:25px/right:25px placement (the `.react-flow__panel` corner offset is
        zeroed for this toolbar in edgFlow.css to preserve render parity).
      */}
      {!exportMode ? (
        <Panel
          position="bottom-right"
          className={`edg-zoom-panel${
            floatingToolbarClassName ? ` ${floatingToolbarClassName}` : ''
          }`}
        >
          <EdgZoomToolbar className={floatingToolbarClassName} name="EDG" />
        </Panel>
      ) : null}
      {/*
        Vertex hint, shown while hovering any edge — port of the old
        `{linkHover && <div className="link-help">…</div>}` in Paper/index.js.
        Rendered through a <Panel> for the same stacking reasons as the zoom
        cluster; pointer-events disabled so it never intercepts canvas input.
      */}
      {interactive && vertexHint ? (
        <Panel position="bottom-left" className="edg-link-help-panel">
          <div className="link-help">{vertexHint}</div>
        </Panel>
      ) : null}
    </ReactFlow>
  );
});

/**
 * Public EdgFlow: wraps the inner flow in a ReactFlowProvider so the toolbar
 * (and any later viewport-reading children) can use React Flow hooks.
 */
export default function EdgFlow(props: EdgFlowProps) {
  return (
    <ReactFlowProvider>
      <EdgFlowInner {...props} />
    </ReactFlowProvider>
  );
}
